// src/pages/Integridade/IntegridadePage.jsx

import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Science as ScienceIcon,
  Psychology as PsychologyIcon,
  FindInPage as FindInPageIcon,
  Insights as InsightsIcon,
  Policy as PolicyIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  Verified as VerifiedIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const Feature = ({ icon, title, text }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon}
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Typography color="text.secondary">{text}</Typography>
    </Paper>
  </motion.div>
);

Feature.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

const CardMini = ({ title, items }) => (
  <Paper sx={{ p: 3, height: '100%' }}>
    <Typography variant="h6" gutterBottom>
      {title}
    </Typography>
    <Stack spacing={1}>
      {items.map((t) => (
        <Stack key={t} direction="row" spacing={1} alignItems="center">
          <ShieldIcon fontSize="small" color="primary" />
          <Typography color="text.secondary">{t}</Typography>
        </Stack>
      ))}
    </Stack>
  </Paper>
);

CardMini.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const IntegridadePage = () => {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          color: 'primary.contrastText',
          background:
            'linear-gradient(135deg, rgba(5,10,25,1) 0%, rgba(8,80,120,1) 60%)',
          py: { xs: 8, md: 12 },
          mb: 6,
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Breadcrumbs sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>
            <Link component={RouterLink} to="/" color="inherit" underline="hover">
              Início
            </Link>
            <Typography color="inherit">Integridade</Typography>
          </Breadcrumbs>

          <Typography variant="h2" component="h1" gutterBottom>
            Integridade Académica
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 960, opacity: 0.9 }}>
            Políticas, auditoria, perfis e permissões — confiança para decisões académicas.
            Anti-plágio, detecção de IA (ensemble) e relatórios explicáveis, num só lugar.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: 'wrap' }}>
            <Chip
              icon={<VerifiedIcon />}
              label="ZenKroll — Produto da DataBase Lda"
              variant="outlined"
            />
            <Chip icon={<SecurityIcon />} label="Trilha de Auditoria Completa" variant="outlined" />
            <Chip icon={<PolicyIcon />} label="Políticas & Limiar" variant="outlined" />
          </Stack>

          <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" color="secondary" component={RouterLink} to="/leads">
              Solicitar Demonstração
            </Button>
            <Button variant="outlined" color="inherit" component={RouterLink} to="/tcc">
              Ver Módulo TCC
            </Button>
          </Box>
        </Container>

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.2,
            backgroundImage:
              'radial-gradient(circle at 10% 0%, rgba(255,200,50,.55) 0 10%, transparent 40%), radial-gradient(circle at 90% -10%, rgba(0,255,220,.4) 0 8%, transparent 30%)',
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ flex: 1 }}>
        {/* Features */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <Feature
              icon={<FindInPageIcon color="primary" />}
              title="Similaridade & Fontes"
              text="Busca em bases internas, repositórios e web pública; percentuais por fonte e links de referência."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Feature
              icon={<PsychologyIcon color="success" />}
              title="Detecção de IA (ensemble)"
              text="Modelo combinado + padrões linguísticos configuráveis por instituição/idioma; scores e classes."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Feature
              icon={<InsightsIcon color="info" />}
              title="Evidências & Explicabilidade"
              text="Trechos destacados, pesos por evidência, parâmetros e notas técnicas anexas ao relatório."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Feature
              icon={<PolicyIcon color="warning" />}
              title="Políticas & Limiar"
              text="Perfis e permissões com políticas por curso/turma; limiares distintos para rascunho/final."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Feature
              icon={<SecurityIcon color="action" />}
              title="Auditoria & Perfis"
              text="Registos de acções com carimbo temporal, utilizador e contexto; trilha completa para decisões."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Feature
              icon={<StorageIcon color="secondary" />}
              title="Dados & Privacidade"
              text="Armazenamento seguro, segregação por instituição, exportações controladas de relatórios e metadados."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Feature
              icon={<CloudIcon color="primary" />}
              title="Escalabilidade"
              text="Processamento assíncrono, filas e cache; adequado a grandes volumes de documentos."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Feature
              icon={<SpeedIcon color="primary" />}
              title="Performance"
              text="Índices por estado/curso/período; normalização e consultas optimizadas para relatórios."
            />
          </Grid>
        </Grid>

        {/* Layout de relatório */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Relatório de Integridade — Estrutura
              </Typography>
              <Stack spacing={1}>
                <Typography color="text.secondary">
                  • Resumo executivo (similaridade, uso de IA, recomendação)
                </Typography>
                <Typography color="text.secondary">
                  • Fontes e percentuais por origem (institucional, web, repositórios)
                </Typography>
                <Typography color="text.secondary">
                  • Evidências destacadas com contexto (trechos alinhados)
                </Typography>
                <Typography color="text.secondary">
                  • Parâmetros e limiares aplicados (políticas activas)
                </Typography>
                <Typography color="text.secondary">• Histórico de versões e anotações</Typography>
                <Typography color="text.secondary">• Registos de auditoria (quem, quando, o quê)</Typography>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip icon={<BarChartIcon />} label="PDF/Excel oficiais" variant="outlined" />
                <Chip icon={<ShieldIcon />} label="Carimbo temporal" variant="outlined" />
                <Chip icon={<VerifiedIcon />} label="Assinatura institucional" variant="outlined" />
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <CardMini
              title="Políticas (exemplos)"
              items={[
                'Limiar de similaridade por curso (ex.: 20–30%)',
                'Sinalização de IA por score/certeza',
                'Regras distintas para rascunho e versão final',
                'Reentrega com plano de correcção',
                'Perfis com responsabilidades (orientador, Juri, direcção)',
              ]}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* FAQ */}
        <Typography variant="h5" gutterBottom>
          Perguntas Frequentes
        </Typography>
        <Grid container spacing={2} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Como interpretar os percentuais de similaridade?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">
                  O percentual indica aproximação textual a fontes conhecidas. A análise deve considerar
                  contexto, citações e políticas do curso. O relatório fornece detalhes e trechos comparados.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>O detector de IA é definitivo?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">
                  Não. É um indicador probabilístico (ensemble). A decisão é institucional, baseada em evidências,
                  políticas e pareceres. Promovemos transparência, não automatismo punitivo.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Que dados são guardados?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">
                  Apenas o necessário para análise e auditoria, respeitando privacidade e contratos.
                  Há segregação por instituição e controlos de exportação.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Quem mantém o ZenKroll?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">
                  O ZenKroll é um produto da <strong>DataBase Lda</strong>, com evolução contínua e suporte dedicado.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>

        {/* CTA */}
        <Paper sx={{ p: { xs: 3, md: 4 }, mb: 8, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Políticas, auditoria, perfis e permissões — confiança para decisões académicas
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Implementa padrões de integridade com relatórios explicáveis e controlo total de perfis.
          </Typography>
          <Stack direction="row" justifyContent="center" spacing={2} flexWrap="wrap">
            <Button variant="contained" color="primary" component={RouterLink} to="/leads">
              Solicitar Demonstração
            </Button>
            <Button variant="outlined" color="primary" component={RouterLink} to="/tcc">
              Explorar Módulo TCC
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default IntegridadePage;
